import { Injectable, OnDestroy } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { RefreshLockService } from '../refresh-lock/refresh-lock.service';
import { JwtUtil } from '@tuition/app/core/util/jwt.util';

@Injectable({
  providedIn: 'root',
})
export class Auth0TokenService implements OnDestroy {
  private _accessToken: string | null = null;
  private _idToken: string | null = null;
  private refreshTimeOut: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly authService: AuthService,
    private readonly refreshLock: RefreshLockService,
  ) {
    // Subscribe to Auth0's ID token claims to get the token
    this.authService.idTokenClaims$.subscribe((token) => {
      if (token) {
        this.idToken = token.__raw;
        console.log('A new ID token has been received');
      } else {
        this.idToken = null;
        this.accessToken = null;
      }
    });

    this.authService.getAccessTokenSilently().subscribe({
      next: (accessToken) => {
        this.accessToken = accessToken;
      },
      error: () => {
        this.accessToken = null;
        this.idToken = null;
      },
    });
  }

  get accessToken(): string | null {
    return this._accessToken;
  }

  set accessToken(token: string | null) {
    if (this._accessToken === token) {
      return; // No change, no need to update
    }

    if (this.refreshTimeOut) {
      clearTimeout(this.refreshTimeOut);
    }

    this._accessToken = token;
    if (token) {
      console.log('Access token has been set');
      this.scheduleTokenRefresh();
    } else {
      console.warn('Access token has been cleared');
    }
  }

  get idToken(): string | null {
    return this._idToken;
  }

  set idToken(token: string | null) {
    this._idToken = token;
  }

  private scheduleTokenRefresh(): void {
    this.authService.getAccessTokenSilently().subscribe((token) => {
      const msUntilExpiration = JwtUtil.msUntilExpiration(token);
      const refreshBefore = 60000;
      const delay = Math.max(msUntilExpiration - refreshBefore, 0);
      if (delay < 0) {
        this.refreshAccessToken();
      } else {
        console.log(`Scheduling token refresh in ${delay} ms`);
        this.refreshTimeOut = setTimeout(() => this.refreshAccessToken(), delay);
      }
    });
  }

  private refreshAccessToken(): void {
    if (!this.refreshLock.tryLock()) {
      console.warn('Refresh lock is already held by another process. Skipping token refresh.');

      return;
    }
    this.authService.getAccessTokenSilently({ cacheMode: 'off' }).subscribe({
      next: (token) => {
        console.log('Access token refreshed.');
        this.accessToken = token;
        this.refreshLock.release();
      },
      error: () => {
        console.warn('Failed to refresh access token');
        this.refreshLock.release();
      },
    });
  }

  ngOnDestroy(): void {
    if (this.refreshTimeOut) {
      clearTimeout(this.refreshTimeOut);
    }
  }
}
